import {Injectable} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {WarningStateService} from '../../ui-state/warning-state/warning-state.service';
import {IUploadedFile} from '../../../components/common/fsx-file-upload/fsx-file-upload.component';
import {
  CodeDTO,
  DeterminationAwardOfCodeDTO,
  DeterminationAwardOfGuid,
  FilingCountyDTO,
  FilingDTO,
  FilingResponseAttorney,
  FilingType,
  GetFilingDocDetermination,
  GetFilingDocDTO,
  GetFilingParticipantDTO,
  ParticipantStatus,
  ParticipantType,
  PostFilingDocDTO,
  UserModel,
  UserTypes
} from '../../../types/api/dot-net-api-types/data-contracts';
import {DocumentApiService} from '../../api/document/document-api.service';
import {catchError, EMPTY, forkJoin, Observable, of, switchMap, take} from 'rxjs';
import {
  ALLOWED_MIME_TYPES,
  documentMaxSizeValidator,
  documentMimeTypeValidator,
  MAX_FILE_SIZE
} from '../../../core/validators/validators';
import {map} from 'rxjs/operators';
import {ErrorsStateService} from '../../ui-state/errors-state/errors-state.service';
import {minActive} from '../../../core/validators/attorneys.validators';
import {Option} from 'fsx-component-library';
import {
  ALPHA_NUMERIC_REGEX,
  CASE_ATTENTION_LINE_REGEX,
  CASE_PHONE_NUMBER_REGEX, CASE_PRISONER_NO_REGEX,
  CASE_SSN_AND_TIN_REGEX,
  CaseTypeIdEnum
} from 'src/constants';
import {UtilitiesService} from '../../utilities/utilities-service.service';
import {FormUtilityService} from '../form-utility/form-utility.service';
import {
  IDeterminationExpenseFormGroup,
  IDeterminationFormGroup,
  IDeterminationVictimizationTypeFormGroup
} from '../existing-case-filing-form/existing-case-filing-form.service';
import {minVictimizationTypeSelected} from '../../../core/validators/array.validators';
import {greaterThanZeroValidator} from '../../../core/validators/greater-than-zero.validator';
import {ecfDeterminationAwardAmountValidator} from '../../../core/validators/determination-award-amount.validators';

/**
 * The claimant attorney form group.
 */
export interface IAttorneyFormGroup {
  applyToAll: FormControl<boolean>;
  name: FormControl<string>;
  attorneyId: FormControl<string>;
  status: FormControl<ParticipantStatus | null>;
  isDefault: FormControl<boolean>;
}

/**
 * The claimant form group.
 */
export interface IClaimantFormGroup {
  name: FormControl<string>;
  ageRange: FormControl<string | null>;
  ssn: FormControl<string>;
  survivingDependantName: FormControl<string>;
  crimeVictimName: FormControl<string>;
  attention: FormControl<string>;
  address1: FormControl<string | null>;
  address2: FormControl<string | null>;
  city: FormControl<string | null>;
  state: FormControl<string | null>;
  zip: FormControl<string | null>;
  phoneNumber: FormControl<string | null>;
  email: FormControl<string | null>;
  attorneys?: FormArray<FormGroup<IAttorneyFormGroup>>;
  id: FormControl<string | null>;
  isProse: FormControl<boolean>;
  inList: FormControl<boolean>;
  proSeUserOptionFC: FormControl<Option>;
  race: FormControl<string | null>;
  gender: FormControl<string | null>;
  numberOfMinors: FormControl<number | null>;
  prisonerNum: FormControl<string | null>;
  warrantNum: FormControl<string | null>;
  warrantNums: FormArray<FormControl<string>>;
  decedent: FormControl<string | null>;
  county: FormControl<string | null>;
  isNAOF: FormControl<boolean>;
}

export interface IAppelleeFormGroup {
  name: FormControl<string>;
  ageRange: FormControl<string | null>;
  ssn: FormControl<string>;
  survivingDependantName: FormControl<string>;
  crimeVictimName: FormControl<string>;
  attention: FormControl<string>;
  address1: FormControl<string | null>;
  address2: FormControl<string | null>;
  city: FormControl<string | null>;
  state: FormControl<string | null>;
  zip: FormControl<string | null>;
  phoneNumber: FormControl<string | null>;
  email: FormControl<string | null>;
  attorneys?: FormArray<FormGroup<IAttorneyFormGroup>>;
  id: FormControl<string | null>;
  isProse: FormControl<boolean>;
  inList: FormControl<boolean>;
  proSeUserOptionFC: FormControl<Option>;
  race: FormControl<string | null>;
  gender: FormControl<string | null>;
  numberOfMinors: FormControl<number | null>;
  prisonerNum: FormControl<string | null>;
  warrantNum: FormControl<string | null>;
  warrantNums: FormArray<FormControl<string>>;
  decedent: FormControl<string | null>;
  county: FormControl<string | null>;
  isNAOF: FormControl<boolean>;
}

/**
 * Interface representing a filing county.
 */
export interface ICountyFormGroup {
  id: FormControl<string>;
  name: FormControl<string>;
}

/**
 * The case information form group.
 */
export interface ICaseInfoFormGroup {    
  caseTypeId: FormControl<string>;
  caseTypeName: FormControl<string>;
  courtId: FormControl<string>;  
  courtName: FormControl<string>;
  countyId: FormControl<string | null>;
  countyName: FormControl<string | null>;
}

export interface IDocumentFormGroup {
  documentId: FormControl<string>;
  file: FormControl<any>;
  fileName: FormControl<string>;
  displayName: FormControl<string>;
  description: FormControl<string>;
  uri?: FormControl<string>;
  status: FormControl<ParticipantStatus>;
  docTypeId: FormControl<string | null>;
  determination?: FormGroup<IDeterminationFormGroup>;
}

export interface IDocumentInfoFormGroup {
  mainDocument: FormGroup<IDocumentFormGroup>;
  supportingDocuments: FormArray<FormGroup<IDocumentFormGroup>>;
  caseDisposition: FormControl<string>;
}

export interface INewCaseFilingFormGroup {
  claimantFormArray: FormArray<FormGroup<IClaimantFormGroup>>;  
  appelleeFormArray: FormArray<FormGroup<IAppelleeFormGroup>>;  
  caseInfoFormGroup: FormGroup<ICaseInfoFormGroup>;
  documentInfoFormGroup: FormGroup<IDocumentInfoFormGroup>;
  dateTimeSelectionFC: FormControl<string | null>;
  feeWaiverFC: FormControl<boolean>;
}

export interface Attorney {
  label: string;
  value: string;
}

/**
 * The respondent selection types.
 */
export enum IRespondentSelectionTypes {
  StateOfIllinois = 'The State Of Tennessee',
  Other = 'Other',
  None = ''
}

@Injectable({
  providedIn: 'root'
})
export class NewCaseFilingFormService {

  constructor(private readonly documentApiService: DocumentApiService,
              private readonly errorsStateService: ErrorsStateService,
              private readonly fb: FormBuilder,
              private readonly formUtilsService: FormUtilityService,
              private readonly utilitiesService: UtilitiesService,
              private warningStateService: WarningStateService) {}

  /**
   * Create a new case filing form group.
   * @param user
   * @param draftFiling
   */
  createNewCaseFilingFormGroup(user: UserModel, draftFiling?: FilingDTO): FormGroup<INewCaseFilingFormGroup> {
    return this.fb.group<INewCaseFilingFormGroup>({
      caseInfoFormGroup: this.initCaseInfoFG(user, draftFiling),
      claimantFormArray: this.initClaimantsFG(user, draftFiling),      
      appelleeFormArray: this.initAppelleesFG(user, draftFiling),
      documentInfoFormGroup: this.initDocumentInfoFG(draftFiling),
      dateTimeSelectionFC: new FormControl(new Date().toISOString(), {nonNullable: true}),
      feeWaiverFC: new FormControl(draftFiling?.feeWaiverRequested || false, {nonNullable: true})
    });
  }
  // -------------------------------------------- Initializers --------------------------------------------
  /**
   * Private method to init the claimant form array.
   * @param user
   * @param draftFiling
   */
  private initClaimantsFG(user: UserModel, draftFiling?: FilingDTO): FormArray<FormGroup<IClaimantFormGroup>>{
    const fa = this.fb.array([] as FormGroup<IClaimantFormGroup>[], [Validators.minLength(1)]);
    if (!!draftFiling) {
      const claimants = <GetFilingParticipantDTO[]>draftFiling.participants?.filter(p => p.type === ParticipantType.Claimant);
      claimants?.forEach(a => {
        this.addClaimant(fa, user, a);
      });
    } else {
      this.addClaimant(fa, user);
    }
    return fa;
  }

  /**
   * Private method to init the claimant form array.
   * @param user
   * @param draftFiling
   */
  private initAppelleesFG(user: UserModel, draftFiling?: FilingDTO): FormArray<FormGroup<IAppelleeFormGroup>>{
    const fa = this.fb.array([] as FormGroup<IAppelleeFormGroup>[], [Validators.minLength(1)]);
    if (!!draftFiling) {
      const appellees = <GetFilingParticipantDTO[]>draftFiling.participants?.filter(p => p.type === ParticipantType.Respondant);
      appellees?.forEach(a => {
        this.addAppellee(fa, user, a);
      });
    } else {
      this.addAppellee(fa, user);
    }
    return fa;
  }


  /**
   * Initialize the case info form group.
   * @param user
   * @param draftFiling
   * @private
   */
  private initCaseInfoFG(user: UserModel, draftFiling?: FilingDTO): FormGroup<ICaseInfoFormGroup> {
    // Initiate the case info form group.
    const fg = this.fb.group({      
    courtId: new FormControl(draftFiling?.courtId, {nonNullable: true, validators: Validators.required}),    
    countyId: new FormControl(draftFiling?.countyId || null, {nonNullable: false}),    
    caseTypeId: new FormControl(draftFiling?.caseTypeId, {nonNullable: true, validators: Validators.required}),    
  } as ICaseInfoFormGroup);

    return fg;
  }

  /**
   * Initialize the document info form group.
   * @private
   */
  public initDocumentInfoFG(draftFiling?: FilingDTO): FormGroup<IDocumentInfoFormGroup> {
    // Initiate the document info form group.
    const fg = this.fb.group({
      mainDocument: this.fb.group({
        file: new FormControl(null, {nonNullable: false, validators: [Validators.required, documentMimeTypeValidator(ALLOWED_MIME_TYPES), documentMaxSizeValidator(MAX_FILE_SIZE)]}),
        fileName: new FormControl('', {nonNullable: true, validators: Validators.required}),
        displayName: new FormControl('', {nonNullable: true, validators: Validators.required}),
        description: new FormControl('', {nonNullable: true, validators: Validators.required}),
        uri: new FormControl('' as string),
        docTypeId: new FormControl(null, {nonNullable: true, validators: Validators.required})
      } as IDocumentFormGroup),
      supportingDocuments: this.fb.array([] as FormGroup<IDocumentFormGroup>[]),
      caseDisposition: new FormControl(draftFiling?.dispositionId || null, {nonNullable: true}),
    } as IDocumentInfoFormGroup, {validators: [ecfDeterminationAwardAmountValidator()]});
    // Set any values from the draft filing if it exists.
    if (!!draftFiling) {
      draftFiling.documents?.forEach(documentDTO => {
        if (documentDTO.mainDocument) {
          fg.controls.mainDocument.controls.fileName.setValue(documentDTO.fileName as string);
          fg.controls.mainDocument.controls.displayName.setValue(documentDTO.displayName as string);
          fg.controls.mainDocument.controls.description.setValue(documentDTO.description as string);
          fg.controls.mainDocument.controls.uri?.setValue(documentDTO.fileURI as string);
          fg.controls.mainDocument.controls.file.setErrors(null);
          fg.controls.mainDocument.controls.docTypeId.setValue(documentDTO.docType as string);

          const determination = documentDTO.determination as GetFilingDocDetermination;
          if (!!determination && !!determination.determination?.id) {
            fg.controls.mainDocument.controls.determination = this.fb.group({
              category: new FormControl(determination.determinationCategory?.id || '', {validators: Validators.required}),
              supplementalNumber: new FormControl(determination.supplementalDeterminationNum || -1, {nonNullable: true}),
              determination: new FormControl(determination.determination?.id, {validators: Validators.required}),
              victimizationTypes: this.fb.array([] as FormGroup<IDeterminationVictimizationTypeFormGroup>[], {validators: minVictimizationTypeSelected(1)}),
              awardTypes: this.fb.array([] as FormGroup<IDeterminationExpenseFormGroup>[]),
              totalAwardAmount: new FormControl(draftFiling.awardAmount || 0, {nonNullable: true}),
            } as IDeterminationFormGroup);

            // Add the victimization types to the form array.
            const vts = determination.determinationVictimTypes as CodeDTO[] || [];
            vts.forEach(vt => {
              fg.controls.mainDocument.controls.determination?.controls.victimizationTypes.push(this.fb.group({
                value: new FormControl(vt.id as string, {nonNullable: true}),
                checked: new FormControl(true, {nonNullable: true}),
                name: new FormControl(vt.name as string, {nonNullable: true}),
              } as IDeterminationVictimizationTypeFormGroup));
            });

            // Add the expenses to the form array.
            let awardValues = determination.determinationAwards as DeterminationAwardOfCodeDTO[] || [];
            awardValues.forEach(av => {
              fg.controls.mainDocument.controls.determination?.controls.awardTypes.push(this.fb.group({
                amount: new FormControl(av.awardValue || 0, {nonNullable: true}),
                typeId: new FormControl(av.award?.id || '', {nonNullable: true}),
                name: new FormControl(av.award?.name || '', {nonNullable: true}),
              } as IDeterminationExpenseFormGroup));
            });
          }
        } else {
          const supportingDocFG = this.fb.group({
            file: new FormControl(null, {nonNullable: false, validators: [Validators.required, documentMimeTypeValidator(ALLOWED_MIME_TYPES), documentMaxSizeValidator(MAX_FILE_SIZE)]}),
            fileName: new FormControl(documentDTO.fileName, {nonNullable: true, validators: Validators.required}),
            displayName: new FormControl(documentDTO.displayName, {nonNullable: true, validators: Validators.required}),
            description: new FormControl(documentDTO.description, {nonNullable: true, validators: Validators.required}),
            uri: new FormControl(documentDTO.fileURI),
            docTypeId: new FormControl(documentDTO.docType),
          } as IDocumentFormGroup);
          supportingDocFG.controls.file.setErrors(null);
          fg.controls.supportingDocuments.push(supportingDocFG);
        }
      });
      /** TODO: update this later */
      // fg.controls.caseDisposition.setValue('');
    }
    return fg;
  }

  /**
   * Clears the file validators
   * @param fileFC
   */
  public clearFileValidators(fileFC: FormControl): void {
    fileFC.clearValidators();
    fileFC.updateValueAndValidity();
  }

  /**
   * Adds the file validators. Defaults to the default allowed mime types.
   * @param fileFC
   * @param allowedMimeTypes
   */
  public addFileValidators(fileFC: FormControl, allowedMimeTypes: string[] = ALLOWED_MIME_TYPES): void {
    fileFC.addValidators([
      Validators.required,
      documentMimeTypeValidator(allowedMimeTypes),
      documentMaxSizeValidator(MAX_FILE_SIZE)
    ]);
    fileFC.updateValueAndValidity();
  }

  // -------------------------------------------- Claimant Methods --------------------------------------------
  /**
   * Helper
   * @param claimantFormArray
   * @param user
   * @param draftClaimant
   */
  addClaimant(claimantFormArray: FormArray<FormGroup<IClaimantFormGroup>>, user: UserModel, draftClaimant?: GetFilingParticipantDTO): void {
    const userType = user?.userTypeCode as UserTypes;
    const claimantFormGroup: FormGroup<IClaimantFormGroup> =   this.fb.group<IClaimantFormGroup>({
      id: new FormControl(draftClaimant?.id || null),
      name: new FormControl(draftClaimant?.name || '', {nonNullable: true, validators: Validators.required}),
      ageRange: new FormControl(draftClaimant?.ageRangeCodeId || null, {nonNullable: true, validators: !!draftClaimant?.ageRangeCodeId ? Validators.required : []}),
      ssn: new FormControl(draftClaimant?.ssn || '', {nonNullable: true, validators: [Validators.pattern(CASE_SSN_AND_TIN_REGEX)]}),
      crimeVictimName: new FormControl(draftClaimant?.crimeVictimName || '', {nonNullable: true}),
      survivingDependantName: new FormControl(draftClaimant?.survivingDependant || '', {nonNullable: true}),
      attention: new FormControl (draftClaimant?.address?.attention || '', {nonNullable: true, validators: [Validators.pattern(CASE_ATTENTION_LINE_REGEX), Validators.maxLength(50)]}),
      address1: new FormControl(draftClaimant?.address?.address1 || '' || null),
      address2: new FormControl(draftClaimant?.address?.address2 || '' || null),
      city: new FormControl(draftClaimant?.address?.city || '' || null),
      state: new FormControl(draftClaimant?.address?.state || '' || null),
      zip: new FormControl(draftClaimant?.address?.zip || '' || null),
      phoneNumber: new FormControl(draftClaimant?.phoneNumber || '' || null),
      email: new FormControl(draftClaimant?.claimantEmail || '' || null),
      isProse: new FormControl(draftClaimant?.isProSeUser || false, {nonNullable: true}),
      isNAOF: new FormControl(draftClaimant?.isNAOF || true, {nonNullable: true}),
      inList: new FormControl((!draftClaimant?.id || draftClaimant?.id === '') && draftClaimant?.isProSeUser === true, {nonNullable: true}),
      proSeUserOptionFC: new FormControl<Option>({label: '', value: ''}, {nonNullable: true}),
      race: new FormControl(draftClaimant?.claimantRaceCodeId || '', {nonNullable: true, validators: !!draftClaimant?.claimantRaceCodeId ? Validators.required : []}),
      gender: new FormControl(draftClaimant?.genderCodeId || '', {nonNullable: true, validators: !!draftClaimant?.genderCodeId ? Validators.required : []}),
      numberOfMinors: new FormControl(!!draftClaimant?.dependentsCount?.toString() && draftClaimant?.dependentsCount >= 0 ? draftClaimant?.dependentsCount : null, {nonNullable: true, validators: [Validators.required, Validators.min(0), Validators.max(9)]}),
      prisonerNum: new FormControl(draftClaimant?.prisonerNum || '', {validators: [
        Validators.pattern(CASE_PRISONER_NO_REGEX),
        Validators.maxLength(15)
      ]}),
      warrantNum: new FormControl(draftClaimant?.warrantNum || '', {validators: [
        Validators.pattern(ALPHA_NUMERIC_REGEX),
        Validators.maxLength(10)
      ]}),
      warrantNums: this.fb.array([] as FormControl<string>[]),
      decedent: new FormControl(draftClaimant?.decedentName || '', {nonNullable: true}),
      county: new FormControl(draftClaimant?.countyCode?.id || null, {nonNullable: true})
    });

    // Populate the warrant numbers array.
    if (draftClaimant?.warrantNums?.length) {
      draftClaimant.warrantNums.forEach(w => {
        claimantFormGroup.controls.warrantNums.push(new FormControl(w, {nonNullable: true}));
      });
    }

    if (draftClaimant?.attorneys) {
      claimantFormGroup.addControl('attorneys', this.fb.array([] as FormGroup<IAttorneyFormGroup>[]));
      draftClaimant.attorneys.forEach(a => {
        this.addClaimantAttorney(claimantFormGroup, user, a);
      });
    } else if (userType === UserTypes.ProSe) {
      if (claimantFormArray.length === 0) {
        const name = user.firstName + (!!user.middleName ? ' ' + user.middleName : '') + ' ' + user.lastName;
        // Pre-populate the first claimant with the user's information.
        claimantFormGroup.controls.name = new FormControl(name || '', {nonNullable: true, validators: Validators.required});
        claimantFormGroup.controls.address1 = new FormControl (user.address1 || '' || null);
        claimantFormGroup.controls.address2 = new FormControl(user.address2 || '' || null);
        claimantFormGroup.controls.city = new FormControl(user.city || '' || null);
        claimantFormGroup.controls.state = new FormControl(user.state || '' || null);
        claimantFormGroup.controls.zip = new FormControl(user.zip || '' || null);
        claimantFormGroup.controls.phoneNumber = new FormControl(user.phoneNumber || '' || null);
        claimantFormGroup.controls.email = new FormControl(user.emailAddress || '' || null);
        claimantFormGroup.controls.id = new FormControl(user.id || null, {nonNullable: true});
        claimantFormGroup.controls.isProse.setValue(true);
      } else {
        claimantFormGroup.controls.isProse.setValue(true);
      }
      if (draftClaimant?.isProSeUser) {
        claimantFormGroup.controls.isProse.setValue(true);
        claimantFormGroup.controls.name.setValue(draftClaimant.name as string);
        claimantFormGroup.controls.id.setValue(draftClaimant.id as string);
      }
    } else {
      claimantFormGroup.addControl('attorneys', this.fb.array([] as FormGroup<IAttorneyFormGroup>[]));
      this.addClaimantAttorney(claimantFormGroup, user);

      if(user.userTypeCode !== UserTypes.Attorney) {
        this.removeAttorneyValidators(claimantFormGroup);
      }
    }
    claimantFormArray.push(claimantFormGroup);
  }

  addAppellee(claimantFormArray: FormArray<FormGroup<IClaimantFormGroup>>, user: UserModel, draftClaimant?: GetFilingParticipantDTO): void {
    const userType = user?.userTypeCode as UserTypes;
    const claimantFormGroup: FormGroup<IClaimantFormGroup> =   this.fb.group<IClaimantFormGroup>({
      id: new FormControl(draftClaimant?.id || null),
      name: new FormControl(draftClaimant?.name || '', {nonNullable: true, validators: Validators.required}),
      ageRange: new FormControl(draftClaimant?.ageRangeCodeId || null, {nonNullable: true, validators: !!draftClaimant?.ageRangeCodeId ? Validators.required : []}),
      ssn: new FormControl(draftClaimant?.ssn || '', {nonNullable: true, validators: [Validators.pattern(CASE_SSN_AND_TIN_REGEX)]}),
      crimeVictimName: new FormControl(draftClaimant?.crimeVictimName || '', {nonNullable: true}),
      survivingDependantName: new FormControl(draftClaimant?.survivingDependant || '', {nonNullable: true}),
      attention: new FormControl (draftClaimant?.address?.attention || '', {nonNullable: true, validators: [Validators.pattern(CASE_ATTENTION_LINE_REGEX), Validators.maxLength(50)]}),
      address1: new FormControl (draftClaimant?.address?.address1 || '' || null),
      address2: new FormControl(draftClaimant?.address?.address2 || '' || null),
      city: new FormControl(draftClaimant?.address?.city || '' || null),
      state: new FormControl(draftClaimant?.address?.state || '' || null),
      zip: new FormControl(draftClaimant?.address?.zip || '' || null),
      phoneNumber: new FormControl(draftClaimant?.phoneNumber || '' || null),
      email: new FormControl(draftClaimant?.claimantEmail || '' || null),
      isProse: new FormControl(draftClaimant?.isProSeUser || false, {nonNullable: true}),
      isNAOF: new FormControl(draftClaimant?.isNAOF || true, {nonNullable: true}),
      inList: new FormControl((!draftClaimant?.id || draftClaimant?.id === '') && draftClaimant?.isProSeUser === true, {nonNullable: true}),
      proSeUserOptionFC: new FormControl<Option>({label: '', value: ''}, {nonNullable: true}),
      race: new FormControl(draftClaimant?.claimantRaceCodeId || '', {nonNullable: true, validators: !!draftClaimant?.claimantRaceCodeId ? Validators.required : []}),
      gender: new FormControl(draftClaimant?.genderCodeId || '', {nonNullable: true, validators: !!draftClaimant?.genderCodeId ? Validators.required : []}),
      numberOfMinors: new FormControl(!!draftClaimant?.dependentsCount?.toString() && draftClaimant?.dependentsCount >= 0 ? draftClaimant?.dependentsCount : null, {nonNullable: true, validators: [Validators.required, Validators.min(0), Validators.max(9)]}),
      prisonerNum: new FormControl(draftClaimant?.prisonerNum || '', {validators: [
        Validators.pattern(CASE_PRISONER_NO_REGEX),
        Validators.maxLength(15)
      ]}),
      warrantNum: new FormControl(draftClaimant?.warrantNum || '', {validators: [
        Validators.pattern(ALPHA_NUMERIC_REGEX),
        Validators.maxLength(10)
      ]}),
      warrantNums: this.fb.array([] as FormControl<string>[]),
      decedent: new FormControl(draftClaimant?.decedentName || '', {nonNullable: true}),
      county: new FormControl(draftClaimant?.countyCode?.id || null, {nonNullable: true})
    });

    // Populate the warrant numbers array.
    if (draftClaimant?.warrantNums?.length) {
      draftClaimant.warrantNums.forEach(w => {
        claimantFormGroup.controls.warrantNums.push(new FormControl(w, {nonNullable: true}));
      });
    }

    if (draftClaimant?.attorneys) {
      claimantFormGroup.addControl('attorneys', this.fb.array([] as FormGroup<IAttorneyFormGroup>[]));
      draftClaimant.attorneys.forEach(a => {
        this.addClaimantAttorney(claimantFormGroup, user, a);
      });
    } else if (userType === UserTypes.ProSe) {
      if (claimantFormArray.length === 0) {
        const name = user.firstName + (!!user.middleName ? ' ' + user.middleName : '') + ' ' + user.lastName;
        // Pre-populate the first claimant with the user's information.
        claimantFormGroup.controls.name = new FormControl(name || '', {nonNullable: true, validators: Validators.required});
        claimantFormGroup.controls.address1 = new FormControl (user.address1 || '' || null);
        claimantFormGroup.controls.address2 = new FormControl(user.address2 || '' || null);
        claimantFormGroup.controls.city = new FormControl(user.city || '' || null);
        claimantFormGroup.controls.state = new FormControl(user.state || '' || null);
        claimantFormGroup.controls.zip = new FormControl(user.zip || '' || null);
        claimantFormGroup.controls.phoneNumber = new FormControl(user.phoneNumber || '' || null);
        claimantFormGroup.controls.email = new FormControl(user.emailAddress || '', {nonNullable: true, validators: Validators.email});
        claimantFormGroup.controls.id = new FormControl(user.id || null, {nonNullable: true});
        claimantFormGroup.controls.isProse.setValue(true);
      } else {
        claimantFormGroup.controls.isProse.setValue(true);
      }
      if (draftClaimant?.isProSeUser) {
        claimantFormGroup.controls.isProse.setValue(true);
        claimantFormGroup.controls.name.setValue(draftClaimant.name as string);
        claimantFormGroup.controls.id.setValue(draftClaimant.id as string);
      }
    } else {
      claimantFormGroup.addControl('attorneys', this.fb.array([] as FormGroup<IAttorneyFormGroup>[]));
      this.addClaimantAttorney(claimantFormGroup, user);

      if(user.userTypeCode !== UserTypes.Attorney) {
        this.removeAttorneyValidators(claimantFormGroup);
      }
    }
    claimantFormArray.push(claimantFormGroup);
  }

  /**
   * Add a claimant attorney to the claimant form group.
   * @param claimantFC
   * @param user
   * @param draftAttorney
   */
  addClaimantAttorney(claimantFC: FormGroup<IClaimantFormGroup>, user: UserModel, draftAttorney?: FilingResponseAttorney): void {
    const attorney = this.fb.group<IAttorneyFormGroup>({
      name: new FormControl(draftAttorney?.name || '', {nonNullable: true}),
      isDefault: new FormControl(false, {nonNullable: true}),
      applyToAll: new FormControl(draftAttorney?.applyToAll || false, {nonNullable: true, validators: Validators.required}),
      attorneyId: new FormControl(draftAttorney?.attorneyId || '', {nonNullable: true, validators: Validators.required}),
      status: new FormControl(ParticipantStatus.Active, {nonNullable: true})
    });
    claimantFC.controls.attorneys?.push(attorney);
  }

   addAppelleeAttorney(appelleeFC: FormGroup<IAppelleeFormGroup>, user: UserModel, draftAttorney?: FilingResponseAttorney): void {
    const attorney = this.fb.group<IAttorneyFormGroup>({
      name: new FormControl(draftAttorney?.name || '', {nonNullable: true}),
      isDefault: new FormControl(false, {nonNullable: true}),
      applyToAll: new FormControl(draftAttorney?.applyToAll || false, {nonNullable: true, validators: Validators.required}),
      attorneyId: new FormControl(draftAttorney?.attorneyId || '', {nonNullable: true, validators: Validators.required}),
      status: new FormControl(ParticipantStatus.Active, {nonNullable: true})
    });
    appelleeFC.controls.attorneys?.push(attorney);
  }

  /**
   * Delete a claimant from the claimant form array.
   * @param claimantFormArray
   * @param index
   */
  deleteClaimant(claimantFormArray: FormArray<FormGroup<IClaimantFormGroup>>, index: number): boolean {
    if (claimantFormArray.length === 1) {
      this.warningStateService.showWarning('You must have at least one claimant.');
      return false;
    }
    claimantFormArray.removeAt(index);
    return true;
  }

  /**
   * Delete a claimant from the claimant form array.
   * @param claimantFormArray
   * @param index
   */
  deleteAppellee(appelleeFormArray: FormArray<FormGroup<IAppelleeFormGroup>>, index: number): boolean {
    if (appelleeFormArray.length === 1) {
      this.warningStateService.showWarning('You must have at least one appellee.');
      return false;
    }
    appelleeFormArray.removeAt(index);
    return true;
  }

  /**
   * Delete a claimant attorney from the claimant attorney form group.
   * @param claimantApppelleeAttorneyFG
   * @param index
   * @param showWarning
   */
  deleteClaimantAttorney(claimantApppelleeAttorneyFG: FormGroup<IClaimantFormGroup>, index: number, showWarning = true): void {
    if (showWarning && claimantApppelleeAttorneyFG.controls.attorneys?.length === 1) {
      this.warningStateService.showWarning('You must have at least one Attorney.');
      return;
    }
    claimantApppelleeAttorneyFG.controls.attorneys?.removeAt(index);
  }

  /**
   * Delete a claimant attorney from the claimant attorney form group.
   * @param claimantApppelleeAttorneyFG
   * @param index
   * @param showWarning
   */
  deleteAppelleeAttorney(appelleeAttorneyFG: FormGroup<IAppelleeFormGroup>, index: number, showWarning = true): void {
    if (showWarning && appelleeAttorneyFG.controls.attorneys?.length === 1) {
      this.warningStateService.showWarning('You must have at least one Attorney.');
      return;
    }
    appelleeAttorneyFG.controls.attorneys?.removeAt(index);
  }

  /**
   * Add the attorney validators to the participant form group.
   * @param claimantFC
   */
  addAttorneyValidators(claimantFC: FormGroup<IClaimantFormGroup>): void {
    claimantFC.controls.attorneys?.addValidators([Validators.required, minActive(1)]);
    claimantFC.controls.attorneys?.updateValueAndValidity();
    claimantFC.updateValueAndValidity();
  }

  /**
   * Remove the attorney validators from the participant form group.
   * @param claimantFC
   */
  removeAttorneyValidators(claimantFC: FormGroup<IClaimantFormGroup>): void {
    // Remove validators from the attorneys FormArray itself
    claimantFC.controls.attorneys?.clearValidators();
    claimantFC.controls.attorneys?.updateValueAndValidity();

    // Remove validators from each control inside each attorney FormGroup
    if (claimantFC.controls.attorneys) {
      (claimantFC.controls.attorneys as FormArray).controls.forEach(attorneyFG => {
        const group = attorneyFG as FormGroup;
        Object.values(group.controls).forEach(control => {
          control.clearValidators();
          control.updateValueAndValidity();
        });
      });
    }

    claimantFC.updateValueAndValidity();
  }
  /**
   * Add a warrant number to the claimant warrant nums form array.
   * @param claimantFG
   */
  addWarrantNumber(claimantFG: FormGroup<IClaimantFormGroup>): void {
    claimantFG.controls.warrantNums.push(new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.pattern(ALPHA_NUMERIC_REGEX),
        Validators.maxLength(20)
      ]
    }));
  }

  /**
   * Add an invoice number to the case info form group.
   * @param caseInfoFG
   */
  addInvoiceNumber(caseInfoFG: FormGroup<ICaseInfoFormGroup>): void {   
  }

  /**
   * Remove a warrant number from the claimant warrant nums form array.
   * @param claimantFG
   * @param index
   */
  removeWarrantNumber(claimantFG: FormGroup<IClaimantFormGroup>, index: number): void {
    claimantFG.controls.warrantNums.removeAt(index);
  }

  /**
   * Remove an invoice number from the case info nums form array.
   * @param caseInfoFG
   * @param index
   */
  removeInvoiceNumber(caseInfoFG: FormGroup<ICaseInfoFormGroup>, index: number): void {
    
  }

  /**
   * Helper method to copy an attorney form group.
   * @param attorneyFG
   */
  copyAttorneyFG(attorneyFG: FormGroup<IAttorneyFormGroup>): FormGroup<IAttorneyFormGroup> {
    return this.fb.group<IAttorneyFormGroup>({
      name: new FormControl(attorneyFG.controls.name.value || '', {nonNullable: true, validators: Validators.required}),
      isDefault: new FormControl(attorneyFG.controls.isDefault.value || false, {nonNullable: true}),
      applyToAll: new FormControl(false, {nonNullable: true, validators: Validators.required}),
      attorneyId: new FormControl(attorneyFG.controls.attorneyId.value || '', {nonNullable: true, validators: Validators.required}),
      status: new FormControl(ParticipantStatus.Active, {nonNullable: true}),
    });
  }

  // -------------------------------------------- Case Information Methods --------------------------------------------
  /**
   * Add a county to the case info form group.
   * @param caseInfoFormGroup
   */
  addCounty(caseInfoFormGroup: FormGroup<ICaseInfoFormGroup>): void {
    // ToDo: 2/5/2025 - Need to refactor this to claimant level
    // caseInfoFormGroup.controls.counties.push(this.fb.group({
    //   id: new FormControl('', {validators: Validators.required}),
    //   name: new FormControl('', {validators: Validators.required})
    // } as ICountyFormGroup));
  }

  /**
   * Delete a county from the case info form group.
   * @param caseInfoFormGroup
   * @param index
   */
  deleteCounty(caseInfoFormGroup: FormGroup<ICaseInfoFormGroup>, index: number): void {
    // ToDo: 2/5/2025 - Need to refactor this to claimant level
    // caseInfoFormGroup.controls.counties.removeAt(index);
  }

  /**
   * Helper method to add claim amount validation to the claim amount form control.
   * @param claimAmountFC
   */
  addClaimAmountValidation(claimAmountFC: FormControl<string>): void {
    claimAmountFC.addValidators(greaterThanZeroValidator());
  }

  /**
   * Helper method to remove claim amount validation from the claim amount form control.
   * @param claimAmountFC
   */
  removeClaimAmountValidation(claimAmountFC: FormControl<string>): void {
    claimAmountFC.setValue('');
    claimAmountFC.removeValidators(greaterThanZeroValidator());
    claimAmountFC.setErrors(null);
    claimAmountFC.markAsPristine();
  }

  // ------------------------------------------ Document Information Methods ------------------------------------------
  /**
   * Add a main document to the new case filing form group.
   * @param documentInfoFG
   * @param uploadedFile
   */
  addMainDocument(documentInfoFG: FormGroup<IDocumentInfoFormGroup>, uploadedFile: IUploadedFile): void {
    documentInfoFG.controls.mainDocument.controls.file.setValue(uploadedFile);
    documentInfoFG.controls.mainDocument.controls.fileName.setValue(uploadedFile.file.name);
    documentInfoFG.controls.mainDocument.controls.displayName.setValue(uploadedFile.file.name);
    // documentInfoFG.controls.mainDocument.controls.status.setValue(ParticipantStatus.Active);
    if (documentInfoFG.controls.mainDocument.controls.description.value === '') {
      documentInfoFG.controls.mainDocument.controls.description.setValue(uploadedFile.file.name.replace(/\.[^/.]+$/, ""));
    }
  }

  /**
   * Delete the main document from the document info form group.
   * @param documentInfoFG
   */
  deleteMainDocument(documentInfoFG: FormGroup<IDocumentInfoFormGroup>): void {
    documentInfoFG.controls.mainDocument.reset();
  }

  /**
   * Add a supporting document control to the document info form group.
   * @param documentInfoFG
   */
  addSupportingDocument(documentInfoFG: FormGroup<IDocumentInfoFormGroup>): void {
    documentInfoFG.controls.supportingDocuments.push(this.fb.group({
      status: new FormControl(ParticipantStatus.Active, {nonNullable: true}),
      documentId: new FormControl('', {nonNullable: true}),
      file: new FormControl(null, {nonNullable: false, validators: [Validators.required, documentMimeTypeValidator(ALLOWED_MIME_TYPES), documentMaxSizeValidator(MAX_FILE_SIZE)]}),
      fileName: new FormControl('', {nonNullable: true, validators: Validators.required}),
      displayName: new FormControl('', {nonNullable: true, validators: Validators.required}),
      description: new FormControl('', {nonNullable: true, validators: Validators.required}),
      docTypeId: new FormControl(null, {nonNullable: true}),
      determination: this.fb.group({

      } as IDeterminationFormGroup),
    }) as FormGroup<IDocumentFormGroup>);
  }

  /**
   * Upload supporting documents to a specific document form control. If more than one file included, creates new form controls.
   * @param documentInfoFG
   * @param supportingDocFC
   * @param files
   */
  uploadSupportingDocuments(documentInfoFG: FormGroup<IDocumentInfoFormGroup>, supportingDocFC: FormGroup<IDocumentFormGroup>, files: IUploadedFile[]): void {
    files.forEach((file, i) => {
      if (i === 0) {
        supportingDocFC.controls.file.setValue(file);
        supportingDocFC.controls.fileName.setValue(file.file.name);
        supportingDocFC.controls.displayName.setValue(file.file.name);
        if (supportingDocFC.controls.description.value === '') {
          supportingDocFC.controls.description.setValue(file.file.name.replace(/\.[^/.]+$/, ""));
        }
      } else {
        this.addSupportingDocument(documentInfoFG);
        const lastSupDoc = documentInfoFG.controls.supportingDocuments.at(documentInfoFG.controls.supportingDocuments.length - 1) as FormGroup<IDocumentFormGroup>;
        lastSupDoc.controls.file.setValue(file);
        lastSupDoc.controls.fileName.setValue(file.file.name);
        lastSupDoc.controls.displayName.setValue(file.file.name);
        if (lastSupDoc.controls.description.value === '') {
          lastSupDoc.controls.description.setValue(file.file.name.replace(/\.[^/.]+$/, ""));
        }
      }
    });
  }

  /**
   * Clears the name of the supporting document form control.
   * @param supportingDocFC
   */
  clearSupportingDocumentName(supportingDocFC: FormGroup<IDocumentFormGroup>): void {
    supportingDocFC.controls.displayName.reset();
    supportingDocFC.controls.displayName.setValue('');
  }

  /**
   * Clears the address fields of all claimants in the claimant form array.
   * @param claimantFA
   * @param clearErrors Optional parameter to clear errors on the address fields.
   */
  clearClaimantsAddress(claimantFA: FormArray<FormGroup<IClaimantFormGroup>>, clearErrors: boolean = false): void {
    claimantFA.controls.forEach(claimantFG => {
      claimantFG.controls.address1.setValue('');
      claimantFG.controls.city.setValue('');
      claimantFG.controls.state.setValue('');
      claimantFG.controls.zip.setValue('');

      if (clearErrors) {
        claimantFG.controls.address1.setErrors(null);
        claimantFG.controls.city.setErrors(null);
        claimantFG.controls.state.setErrors(null);
        claimantFG.controls.zip.setErrors(null);

        claimantFG.controls.address1.markAsPristine();
        claimantFG.controls.city.markAsPristine();
        claimantFG.controls.state.markAsPristine();
        claimantFG.controls.zip.markAsPristine();
      }
    })
  }

  /**
   * Delete a supporting document from the document info form group.
   * @param documentInfoFG
   * @param index
   */
  deleteSupportingDocument(documentInfoFG: FormGroup<IDocumentInfoFormGroup>, index: number): void {
    documentInfoFG.controls.supportingDocuments.removeAt(index);
  }

  // -------------------------------------------- Build DTO Methods --------------------------------------------
  /**
   * Build the new case filing DTO to be sent as the data appended to the api request's body.
   * @param newCaseFilingFG The new case filing form group.
   * @param isDraft Boolean that indicates if the filing is a draft.
   */
  buildNewCaseFilingDTO(newCaseFilingFG: FormGroup<INewCaseFilingFormGroup>, isDraft = false): Observable<FilingDTO> {
    const claimants = this.buildPostFilingClaimantDTO(newCaseFilingFG.controls.claimantFormArray, isDraft);
    const appellees = this.buildPostFilingAppelleeDTO(newCaseFilingFG.controls.appelleeFormArray, isDraft);

    // Get the full objects from the form group    
    const caseInfo = newCaseFilingFG.controls.caseInfoFormGroup.value;
    const court: CodeDTO = { id: caseInfo.courtId, name: caseInfo.courtName };
    const county: CodeDTO = { id: caseInfo.countyId ?? undefined, name: caseInfo.countyName ?? undefined };
    const caseType: CodeDTO = { id: caseInfo.caseTypeId, name: caseInfo.caseTypeName };

    console.log(caseInfo);

    const filingDTO: FilingDTO = {
      filingType: FilingType.InitialFiling,      
      dateSubmitted: newCaseFilingFG.controls.dateTimeSelectionFC.value || null,
      dateSubmittedActual: null,
      participants:  [...claimants, ...appellees],      
      documents: [],
      caseTypeId: newCaseFilingFG.value.caseInfoFormGroup?.caseTypeId || null, 
      courtId: newCaseFilingFG.value.caseInfoFormGroup?.courtId || undefined,           
      countyId: newCaseFilingFG.value.caseInfoFormGroup?.countyId || null      
    };

    return this.buildPostFilingDocumentDTOs(newCaseFilingFG.controls.documentInfoFormGroup).pipe(
      take(1),
      map(docsDTOs => {
        filingDTO.documents = docsDTOs;
        return filingDTO;
      }),
      catchError(err => {
        console.error('An error occurred while building document DTOs:', err);
        this.errorsStateService.showError('An error occurred while building document DTOs.');
        return of(filingDTO);
      })
    );
  }

  /**
   * Build the post filing claimants DTO.
   * @param claimantFormArray The claimants form array to build from.
   * @param isDraft Boolean that indicates if the filing is a draft.
   */
  buildPostFilingAppelleeDTO(appelleeFormArray: FormArray<FormGroup<IAppelleeFormGroup>>, isDraft = false): GetFilingParticipantDTO[] {
    return appelleeFormArray.controls.map(c => {      
      const appelleeDTO: GetFilingParticipantDTO = {
        type: ParticipantType.Respondant,
        name: c.value.name,
        id: c.value.id,
        ssn: isDraft ? null : this.utilitiesService.removeNonNumericCharacters(c.value.ssn || '') || null,
        address: {
          address1: c.value.address1,
          address2: c.value.address2,
          city: c.value.city,
          state: c.value.state,
          zip: c.value.zip,
          attention: c.value.attention
        },
        phoneNumber: c.value.phoneNumber,
        claimantEmail: c.value.email,
        isProSeUser: c.value.isProse,
        attorneys: c.value.attorneys?.map(a => {
          return {
            name: a.name || '',
            attorneyId: a.attorneyId || null,
            applyToAll: a.applyToAll,
          } as FilingResponseAttorney;
        }) || [],
        // ToDo: New fields for IL SOS
        ageRangeCodeId: c.value.ageRange === '' ? null : c.value.ageRange,
        survivingDependant: c.value.survivingDependantName,
        crimeVictimName: c.value.crimeVictimName || null,
        claimantRaceCodeId: (c.value.race === '' || c.value.race === 'null') ? null : c.value.race,
        genderCodeId: c.value.gender === '' ? null : c.value.gender,
        dependentsCount: (Number(c.value.numberOfMinors) as number) >= 0 ? Number(c.value.numberOfMinors) as number : null,
        prisonerNum: c.value.prisonerNum || null,
        warrantNum: c.value.warrantNum || null,
        descendentName: c.value.decedent || null,
        countyCode: (c.value.county === '' || c.value.county === 'null') ? { id: null } : ({ id: c.value.county } as CodeDTO),
        county: c.value.county === '' || c.value.county === 'null' ? null : c.value.county,
      } as GetFilingParticipantDTO;

      // Setup Warrant Numbers when applicable
      const warrantNumbers = c.value.warrantNums?.filter(wn => !!wn && wn !== '') as string[] || [];
      appelleeDTO.warrantNums = warrantNumbers.length ? warrantNumbers : [];

      // ToDo: This is a change required by Ryan for the prose user to be able to have this filing as an option in the dropdown.
      if (appelleeDTO.isProSeUser) {
        appelleeDTO.id = null;
        appelleeDTO.userId = c.value.id;
      }

      if(c.value.isNAOF == true)
        appelleeDTO.attorneys = [];      
      return appelleeDTO;
    });
  }

  /**
   * Build the post filing claimants DTO.
   * @param claimantFormArray The claimants form array to build from.
   * @param isDraft Boolean that indicates if the filing is a draft.
   */
  buildPostFilingClaimantDTO(claimantFormArray: FormArray<FormGroup<IClaimantFormGroup>>, isDraft = false): GetFilingParticipantDTO[] {
    return claimantFormArray.controls.map(c => {      
      const claimantDTO: GetFilingParticipantDTO = {
        type: ParticipantType.Claimant,
        name: c.value.name,
        id: c.value.id,
        ssn: isDraft ? null : this.utilitiesService.removeNonNumericCharacters(c.value.ssn || '') || null,
        address: {
          address1: c.value.address1,
          address2: c.value.address2,
          city: c.value.city,
          state: c.value.state,
          zip: c.value.zip,
          attention: c.value.attention
        },
        phoneNumber: c.value.phoneNumber,
        claimantEmail: c.value.email,
        isProSeUser: c.value.isProse,
        attorneys: c.value.attorneys?.map(a => {
          return {
            name: a.name || '',
            attorneyId: a.attorneyId || null,
            applyToAll: a.applyToAll,
          } as FilingResponseAttorney;
        }) || [],
        // ToDo: New fields for IL SOS
        ageRangeCodeId: c.value.ageRange === '' ? null : c.value.ageRange,
        survivingDependant: c.value.survivingDependantName,
        crimeVictimName: c.value.crimeVictimName || null,
        claimantRaceCodeId: (c.value.race === '' || c.value.race === 'null') ? null : c.value.race,
        genderCodeId: c.value.gender === '' ? null : c.value.gender,
        dependentsCount: (Number(c.value.numberOfMinors) as number) >= 0 ? Number(c.value.numberOfMinors) as number : null,
        prisonerNum: c.value.prisonerNum || null,
        warrantNum: c.value.warrantNum || null,
        descendentName: c.value.decedent || null,
        countyCode: (c.value.county === '' || c.value.county === 'null') ? { id: null } : ({ id: c.value.county } as CodeDTO),
        county: c.value.county === '' || c.value.county === 'null' ? null : c.value.county,
      } as GetFilingParticipantDTO;

      // Setup Warrant Numbers when applicable
      const warrantNumbers = c.value.warrantNums?.filter(wn => !!wn && wn !== '') as string[] || [];
      claimantDTO.warrantNums = warrantNumbers.length ? warrantNumbers : [];

      // ToDo: This is a change required by Ryan for the prose user to be able to have this filing as an option in the dropdown.
      if (claimantDTO.isProSeUser) {
        claimantDTO.id = null;
        claimantDTO.userId = c.value.id;
      }

      if(c.value.isNAOF == true)
        claimantDTO.attorneys = [];      

      return claimantDTO;
    });
  }

  /**
   * Builds the post filing counties DTO.
   */
  buildPostFilingCountiesDTO(countiesFG: FormArray<FormGroup<ICountyFormGroup>>): FilingCountyDTO[] {
    return countiesFG.controls
      .filter(c => c.controls.id.value.trim() !== '' && c.controls.name.value.trim() !== '')
      .map(c => ({
        id: c.value.id === 'null' ? null : c.value.id,
        name: c.controls.name.value as string
      } as FilingCountyDTO));
  }

  /**
   * Builds the post filing documents DTO.
   * @param documentsFG The document info form group to build from.
   * @param isDraft
   */
  buildPostFilingDocumentDTOs(documentsFG: FormGroup<IDocumentInfoFormGroup>, isDraft = false): Observable<PostFilingDocDTO[]> {
    // Main document DTO Observable
    const mainDocDTO$: Observable<PostFilingDocDTO> =  !!documentsFG.controls.mainDocument.controls.file.value ?
      this.buildNewDocumentDTO(documentsFG.controls.mainDocument, true, isDraft) :
      this.buildDraftDocumentDTO(documentsFG.controls.mainDocument, true, isDraft)
    ;
    // Supporting documents DTO Observables
    const supportingDocsDTOs$: Observable<PostFilingDocDTO>[] = [];
    documentsFG.controls.supportingDocuments.controls.forEach(supportingDocFG => {
      const supportingDocDTO: Observable<PostFilingDocDTO> = !!supportingDocFG.controls.file.value ?
        this.buildNewDocumentDTO(supportingDocFG, false, isDraft) :
        this.buildDraftDocumentDTO(supportingDocFG, false, isDraft)
      ;
      supportingDocsDTOs$.push(supportingDocDTO);
    });
    return forkJoin([mainDocDTO$, ...supportingDocsDTOs$]);
  }

  /**
   * Private helper method to build a draft document DTO.
   * @param documentFG
   * @param isMain
   * @param isDraft
   * @private
   */
  private buildDraftDocumentDTO(documentFG: FormGroup<IDocumentFormGroup>, isMain: boolean, isDraft = false): Observable<GetFilingDocDTO> {
    const postFilingDocDTO: GetFilingDocDTO = {
      mainDocument: isMain,
      fileName: documentFG.controls.fileName.value,
      displayName: documentFG.controls.displayName.value,
      description: documentFG.controls.description.value,
      fileURI: documentFG.controls.uri?.value || '',
      docType: documentFG.value.docTypeId === '' ? null : documentFG.value.docTypeId,
    };
    if (!!documentFG.controls.determination) {
      if (isDraft) {
        this.buildDraftDeterminationDTO(documentFG, postFilingDocDTO);
      } else {
        this.buildDeterminationDTO(documentFG, postFilingDocDTO);
      }
    }
    return of(postFilingDocDTO);
  }

  /**
   * Private helper method to build a determination DTO.
   * @param documentFG
   * @param getFilingDocDTO
   * @private
   */
  private buildDraftDeterminationDTO(documentFG: FormGroup<IDocumentFormGroup>, getFilingDocDTO: GetFilingDocDTO): void {
    let victimTypes: CodeDTO[] = [];
    if (!!documentFG.value.determination?.victimizationTypes && documentFG.value.determination.victimizationTypes.length) {
      victimTypes = documentFG.value.determination?.victimizationTypes
        .filter(v => v.checked)
        .map(v => { return { id: v.value } as CodeDTO }) as CodeDTO[]
      ;
    }

    let awardValues: DeterminationAwardOfCodeDTO[] = [];
    if (!!documentFG.value.determination?.awardTypes && documentFG.value.determination.awardTypes.length) {
      awardValues = documentFG.value.determination?.awardTypes
        .filter(v => !!v.amount && v.amount > 0)
        .map(v => {
          return {
            award: { id: v.typeId } as CodeDTO,
            awardValue: v.amount
          } as DeterminationAwardOfCodeDTO;
        }) as DeterminationAwardOfCodeDTO[]
      ;
    }

    getFilingDocDTO.determination = {
      determinationCategory: documentFG.value.determination?.category !== '' ? {id: documentFG.value.determination?.category} as CodeDTO : null,
      determination: documentFG.value.determination?.determination !== '' ? {id: documentFG.value.determination?.determination} as CodeDTO : null,
      // @ts-ignore
      determinationVictimTypes: victimTypes,
      // @ts-ignore
      determinationAwards: awardValues,
      supplementalDeterminationNum: documentFG.value.determination?.supplementalNumber === -1 ? null : documentFG.value.determination?.supplementalNumber
    };
  }


  /**
   * Private helper method to build a determination DTO.
   * @param documentFG
   * @param postFilingDocDTO
   * @private
   */
  private buildDeterminationDTO(documentFG: FormGroup<IDocumentFormGroup>, postFilingDocDTO: PostFilingDocDTO): void {
    let victimTypes: string[] = [];
    if (!!documentFG.value.determination?.victimizationTypes && documentFG.value.determination.victimizationTypes.length) {
      victimTypes = documentFG.value.determination?.victimizationTypes
        .filter(v => v.checked)
        .map(v => v.value) as string[]
      ;
    }

    let awardValues: DeterminationAwardOfGuid[] = [];
    if (!!documentFG.value.determination?.awardTypes && documentFG.value.determination.awardTypes.length) {
      awardValues = documentFG.value.determination?.awardTypes
        .filter(v => !!v.amount && v.amount > 0)
        .map(v => {
          return {
            award: v.typeId,
            awardValue: v.amount
          } as DeterminationAwardOfGuid
        }) as DeterminationAwardOfGuid[]
      ;
    }
    postFilingDocDTO.determination = {
      determinationCategoryId: documentFG.value.determination?.category || null,
      supplementalDeterminationNum: documentFG.value.determination?.supplementalNumber === -1 ? null : documentFG.value.determination?.supplementalNumber,
      determinationId: documentFG.value.determination?.determination,
      determinationVictimTypes: victimTypes.length ? victimTypes : [],
      determinationAwards: awardValues.length ? awardValues : []
    };
  }

  /**
   * Private helper method to build a document DTO. Will first write the file and then return the DTO with the appropriate URI
   * @param documentFG
   * @param isMain
   * @param isDraft
   * @private
   */
  private buildNewDocumentDTO(documentFG: FormGroup<IDocumentFormGroup>, isMain: boolean, isDraft = false): Observable<PostFilingDocDTO | GetFilingDocDTO> {
    return this.documentApiService.getDocumentUri().pipe(
      take(1),
      switchMap(uri => {
        if (!!documentFG.controls.file.value) {
          const file = documentFG.controls.file.value.file;
          return this.documentApiService.uploadDocument2(uri.uri, file).pipe(
            map(r => {
              if (!isDraft) {
                const postFilingDocDTO: PostFilingDocDTO = {
                  mainDocument: isMain,
                  fileName: documentFG.controls.fileName.value,
                  displayName: documentFG.controls.displayName.value,
                  description: documentFG.controls.description.value,
                  fileURI: uri.uri,
                  docType: !documentFG.value.docTypeId ? null : documentFG.value.docTypeId
                };
                if (!!documentFG.controls.determination) {
                  this.buildDeterminationDTO(documentFG, postFilingDocDTO);
                }
                return postFilingDocDTO;
              } else {
                const getFilingDocDTO: GetFilingDocDTO = {
                  mainDocument: isMain,
                  fileName: documentFG.controls.fileName.value,
                  displayName: documentFG.controls.displayName.value,
                  description: documentFG.controls.description.value,
                  fileURI: uri.uri,
                  docType: !documentFG.value.docTypeId ? null : documentFG.value.docTypeId
                };
                if (!!documentFG.controls.determination) {
                  this.buildDraftDeterminationDTO(documentFG, getFilingDocDTO);
                }
                return getFilingDocDTO;
              }
            }))
          ;
        }
        return EMPTY;
      }),
      catchError(err => {
        console.error('Error occurred setting DTO:', err);
        return EMPTY;
      }))
    ;
  }
}
